import { Fragment, useState, useEffect } from "react";
import { format } from "date-fns";
import classNames from "classnames/bind";
import styles from "./CaseChatCard.module.scss";
import { BreadCrumb, FormInput, FilePreview } from "~/components";
import { Send, Search, Headset, CheckCircle2, XCircle, Bell, File } from "lucide-react";
import CaseChatCard from "./component/CaseChatCard";
import CasePendingCard from "./component/CasePendingCard";
import ChatMessageGroup from "./component/ChatMessageGroup";
import { useForm } from "react-hook-form";
import { faTriangleExclamation, faArrowLeft, faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import useSupportCaseAdmin from "./useSupportCaseAdmin";
import { getAssetsFileUrl } from '~/utils/assetsHelper';

const cx = classNames.bind(styles);

const breadCrumbItem = [
    { text: "AI Chatbot Page", to: "/ai-chatbot/administrator", isActive: false },
    { text: "Support Ticket", to: "", isActive: true },
];

function SupportCaseAdmin({ isWidget = false, onBadgeUpdate }) {
    const noChatImage = getAssetsFileUrl('status', 'no-chat.png');
    const noCaseImage = getAssetsFileUrl('status', 'no-case.png');

    const [searchQuery, setSearchQuery] = useState("");

    const {
        activeTab,
        setActiveTab,
        pendingCase,
        chattingCase,
        messageGroup,
        selectedChat,
        messageInput,
        setMessageInput,
        handleMessageInputChange,
        handleStopTyping,
        customerTyping,
        showModal,
        setShowModal,
        setIsFocused,
        chatEndRef,
        showCaseFileModal,
        setShowCaseFileModal,
        caseFiles,
        handleOnClickChattingCase,
        handleOnClickPendingCase,
        handleKeyDown,
        handleOnClickAccept,
        handleCloseChat,
        handleOpenCaseFile,
        rejectSupportCase,
        handleBackToList
    } = useSupportCaseAdmin();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors }
    } = useForm();

    // Call onBadgeUpdate whenever chattingCase or pendingCase changes
    useEffect(() => {
        if (onBadgeUpdate) {
            const unreadCount = chattingCase.reduce((sum, item) => sum + (item.CountCustomerChatUnread || 0), 0);
            const pending = pendingCase.length;
            onBadgeUpdate(unreadCount, pending);
        }
    }, [chattingCase, pendingCase, onBadgeUpdate]);

    const handleOnClickReject = (id, userId) => {
        reset({
            SupportCaseId: id,
            UserId: userId,
            RejectReason: ""
        });
        setShowModal(true);
    };

    const onSubmitFrom = (data) => {
        rejectSupportCase(data, reset);
    };

    const pendingCount = pendingCase.length;

    const renderSidebarContent = () => {
        const cases = activeTab === "PendingTab" ? pendingCase : chattingCase;
        
        const filteredCases = cases.filter(c => {
            const nameMatch = c.CustomerName?.toLowerCase().includes(searchQuery.toLowerCase());
            const subjectMatch = (c.Subject || c.CaseType)?.toLowerCase().includes(searchQuery.toLowerCase());
            return !searchQuery || nameMatch || subjectMatch;
        });

        return (
            <div className={cx("case-tab-content", "scrollbar")}>
                {filteredCases.length <= 0 && (
                    <div className="text-center py-5 px-4">
                        <div className="mx-auto mb-3 rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: "48px", height: "48px" }}>
                            <Headset className="text-secondary" size={24} />
                        </div>
                        <p className="fs-9 fw-bold text-secondary mb-1">No tickets</p>
                        <p className="fs-10 text-muted">
                            No {activeTab === "PendingTab" ? "pending" : "active"} support requests
                        </p>
                    </div>
                )}

                {filteredCases.map((item, index) =>
                    activeTab === "PendingTab" ? (
                        <CasePendingCard
                            key={index}
                            avatarUrl={item.CustomerAvatarUrl}
                            caseCreatedDate={format(new Date(item.CreatedDate), "dd-MM-yyyy hh:mm a")}
                            castType={item.CaseType}
                            senderName={item.CustomerName}
                            active={item.SupportCaseId === selectedChat.SupportCaseId}
                            onClick={() => handleOnClickPendingCase(item)}
                        />
                    ) : (
                        <CaseChatCard
                            key={index}
                            avatarUrl={item.CustomerAvatarUrl}
                            status={item.CountCustomerChatUnread > 0 ? "awaiting" : "active"}
                            active={item.CaseSessionId === selectedChat.CaseSessionId}
                            supportCaseId={`#${item.SupportCaseId}`}
                            caseTitle={item.Subject}
                            lastMessage={item.LastChatCustomer}
                            senderName={item.CustomerName}
                            onClick={() => handleOnClickChattingCase(item.CaseSessionId)}
                            unreadCount={item.CountCustomerChatUnread}
                        />
                    )
                )}
            </div>
        );
    };

    return (
        <Fragment>
            {!showModal && !showCaseFileModal ? (
                <Fragment>
                    {!isWidget && (
                        <div className="d-flex align-items-center justify-content-between mb-4 mt-2">
                            <div>
                                <h2 className="fw-bolder text-body-emphasis mb-2" style={{ letterSpacing: '-0.5px', fontSize: '1.75rem' }}>
                                    Support Ticket Assistant
                                </h2>
                                <BreadCrumb breadCrumbItem={breadCrumbItem}/>
                            </div>
                        </div>
                    )}
                    
                    <div className={cx("chat-container", { "is-widget": isWidget, "has-chat": Object.keys(selectedChat).length > 0 })}>
                        {/* Left Sidebar */}
                        <div className={cx("chat-sidebar")}>
                            <div className={cx("sidebar-header")}>
                                {!isWidget && (
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <h1 className="fs-8 fw-bold text-dark d-flex align-items-center gap-2 mb-0">
                                            <Headset size={20} style={{ color: '#ff9900' }} /> Customer Support
                                        </h1>
                                        {pendingCount > 0 && (
                                            <span className="d-flex align-items-center gap-1 px-2 py-1 bg-danger-subtle text-danger fs-10 fw-bold rounded-pill border border-danger-subtle">
                                                <Bell size={12} /> {pendingCount} waiting
                                            </span>
                                        )}
                                    </div>
                                )}
                                
                                <div className={cx("search-container")}>
                                    <Search size={16} />
                                    <input 
                                        type="text" 
                                        placeholder="Search..." 
                                        value={searchQuery}
                                        onChange={e => setSearchQuery(e.target.value)}
                                    />
                                </div>
                            </div>
                            
                            <div className={cx("chat-sidebar-tab")}>
                                {["PendingTab", "ChatTab"].map((tab) => {
                                    const count = tab === "PendingTab" ? pendingCase.length : chattingCase.length;
                                    return (
                                        <button
                                            key={tab}
                                            className={cx("tab-btn", tab === "PendingTab" ? "amber" : "", activeTab === tab ? "active" : "")}
                                            onClick={() => setActiveTab(tab)}
                                        >
                                            {tab === "PendingTab" ? "Pending" : "Active"}
                                            {count > 0 && (
                                                <span 
                                                    className="ms-1 d-inline-flex align-items-center justify-content-center rounded-circle fw-bold"
                                                    style={{
                                                        width: "20px",
                                                        height: "20px",
                                                        fontSize: "0.65rem",
                                                        backgroundColor: tab === "PendingTab" ? "#fff3cd" : "#d1e7dd",
                                                        color: tab === "PendingTab" ? "#856404" : "#0f5132",
                                                    }}
                                                >
                                                    {count}
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                            
                            {renderSidebarContent()}
                        </div>

                        {/* Right Chat Area */}
                        <div className={cx("card-chat")}>
                            {Object.keys(selectedChat).length > 0 ? (
                                <Fragment>
                                    {/* Chat Header */}
                                    <div className={cx("chat-header")}>
                                        <div className="d-flex align-items-center gap-2 flex-grow-1 overflow-hidden pe-2">
                                            {isWidget && (
                                                <button className="btn btn-sm btn-light border-0 flex-shrink-0" onClick={handleBackToList}>
                                                    <FontAwesomeIcon icon={faChevronLeft} />
                                                </button>
                                            )}
                                            <div className="rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: "36px", height: "36px" }}>
                                                {selectedChat.CustomerAvatarUrl ? (
                                                    <img src={selectedChat.CustomerAvatarUrl} alt="avatar" className="w-100 h-100" style={{objectFit:"cover"}}/>
                                                ) : (
                                                    <span className="fw-bold fs-7 text-secondary">{selectedChat.CustomerName?.charAt(0) || "?"}</span>
                                                )}
                                            </div>
                                            <div style={{ minWidth: 0, overflow: 'hidden' }}>
                                                <h3 className="fs-9 fw-bold text-dark mb-0 text-truncate">{selectedChat.CustomerName || "Customer"}</h3>
                                                <div className="d-flex align-items-center gap-2 fs-10 text-muted mt-1 text-truncate">
                                                    <span className="text-truncate">{selectedChat.CustomerEmail || "No Email"}</span>
                                                    {selectedChat.CaseType && (
                                                        <Fragment>
                                                            <span className="flex-shrink-0">•</span>
                                                            <span className="fw-bold text-primary text-truncate">{selectedChat.CaseType}</span>
                                                        </Fragment>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="d-flex align-items-center gap-1 flex-shrink-0">
                                            {/* Action Buttons */}
                                            {selectedChat.Status?.toLowerCase() === "waiting" ? (
                                                <Fragment>
                                                    <button
                                                        onClick={() => handleOnClickReject(selectedChat.TicketId || selectedChat.SupportCaseId, selectedChat.UserId)}
                                                        className="btn btn-sm btn-light text-secondary fw-bold d-flex align-items-center gap-1 fs-10"
                                                    >
                                                        <XCircle size={14} /> Reject
                                                    </button>
                                                    <button
                                                        onClick={() => handleOnClickAccept(selectedChat.TicketId || selectedChat.SupportCaseId)}
                                                        className="btn btn-sm btn-success text-white fw-bold d-flex align-items-center gap-1 fs-10"
                                                    >
                                                        <CheckCircle2 size={14} /> Accept
                                                    </button>
                                                </Fragment>
                                            ) : (
                                                <Fragment>
                                                    {caseFiles && caseFiles.length > 0 && (
                                                        <button
                                                            onClick={handleOpenCaseFile}
                                                            className="btn btn-sm btn-light text-secondary fw-bold d-flex align-items-center gap-1 fs-10 me-1"
                                                            title="View attached files"
                                                        >
                                                            <File size={14} /> Files
                                                        </button>
                                                    )}
                                                    <button
                                                        onClick={handleCloseChat}
                                                        className="btn btn-sm btn-light text-secondary fw-bold d-flex align-items-center gap-1 fs-10"
                                                    >
                                                        <XCircle size={14} /> Close Session
                                                    </button>
                                                </Fragment>
                                            )}
                                        </div>
                                    </div>

                                    {/* Messages Area */}
                                    <div className={cx("chat-messages", "scrollbar")}>
                                        {messageGroup.length === 0 ? (
                                            <div className="text-center py-5 fs-9 text-muted mt-4">
                                                {selectedChat.Status?.toLowerCase() === "waiting" ? "Chat not started. Click Accept to begin." : "No messages in this session yet."}
                                            </div>
                                        ) : (
                                            messageGroup.map((group, index) => {
                                                const isReceiver = group[0].SupporterOrCustomer === "Customer";
                                                const dateStr = group[group.length - 1].CreatedDate;
                                                
                                                let time = "";
                                                if (dateStr) {
                                                    const str = String(dateStr);
                                                    const parsedDate = new Date(str + (str.endsWith("Z") ? "" : "Z"));
                                                    if (!isNaN(parsedDate.getTime())) {
                                                        time = parsedDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                                                    }
                                                }
                                                
                                                return (
                                                    <ChatMessageGroup
                                                        key={index}
                                                        group={group}
                                                        isReceiver={isReceiver}
                                                        time={time}
                                                        avatar={selectedChat.CustomerAvatarUrl}
                                                        isLast={(messageGroup.length - 1) === index}
                                                    />
                                                );
                                            })
                                        )}
                                        {customerTyping && selectedChat.Status?.toLowerCase() === "active" && (
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <div className="rounded-circle overflow-hidden bg-light d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: "24px", height: "24px" }}>
                                                    {selectedChat.CustomerAvatarUrl ? (
                                                        <img src={selectedChat.CustomerAvatarUrl} alt="avatar" className="w-100 h-100" style={{objectFit:"cover"}}/>
                                                    ) : (
                                                        <span className="fw-bold fs-9 text-secondary">{selectedChat.CustomerName?.charAt(0) || "?"}</span>
                                                    )}
                                                </div>
                                                <div className={cx("msg-bubble", "msg-customer")}>
                                                    <div className={cx("typingIndicator")}>
                                                        <span />
                                                        <span />
                                                        <span />
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                        <div ref={chatEndRef}></div>
                                    </div>

                                    {/* Input Area */}
                                    {selectedChat.Status?.toLowerCase() !== "waiting" ? (
                                        <div className={cx("chat-footer")}>
                                            <div className={cx("input-wrapper")}>
                                                <textarea
                                                    className={cx("chat-input")}
                                                    placeholder="Type a message..."
                                                    value={messageInput}
                                                    onChange={(e) => {
                                                        handleMessageInputChange(e.target.value);
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === "Enter" && !e.shiftKey) {
                                                            e.preventDefault();
                                                            const event = { key: "Enter", preventDefault: () => {} };
                                                            handleKeyDown(event);
                                                        }
                                                    }}
                                                    onFocus={() => setIsFocused(true)}
                                                    onBlur={() => {
                                                        setIsFocused(false);
                                                        if (typeof handleStopTyping === 'function') handleStopTyping();
                                                    }}
                                                    rows={1}
                                                />
                                                <div className={cx("input-actions")}>
                                                    <button
                                                        className={cx("send")}
                                                        onClick={(e) => {
                                                            const event = { key: "Enter", preventDefault: () => {} };
                                                            handleKeyDown(event);
                                                        }}
                                                        disabled={!messageInput.trim()}
                                                        title="Send message"
                                                    >
                                                        <Send size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="bg-warning-subtle border-top py-2 px-3 text-center">
                                            <p className="fs-10 fw-bold text-warning-emphasis mb-0">
                                                ⏳ Request is waiting to be accepted. Click "Accept" to start support.
                                            </p>
                                        </div>
                                    )}
                                </Fragment>
                            ) : (
                                <div className={cx("empty-state")}>
                                    <div className={cx("icon-container")}>
                                        <Headset />
                                    </div>
                                    <h3 className="fs-7 fw-bold text-dark mb-2">Support Center</h3>
                                    <p className="fs-9 text-muted max-w-sm mb-0">
                                        Select a support request from the left list to start chatting with the customer.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </Fragment>
            ) : showModal ? (
                /* Original Modal UI for Rejecting Support Ticket */
                <div className="modern-form-wrapper rounded-4 bg-white pb-3">
                    <form onSubmit={handleSubmit(onSubmitFrom)} id="formReject">
                        <div className="px-4 pt-4 px-md-5 pt-md-5">
                            {/* Header Section */}
                            <div className="d-flex align-items-center mb-4 pb-1">
                                <button 
                                    className="btn btn-light border-0 text-muted hover-text-dark rounded-circle me-3 transition-base d-flex align-items-center justify-content-center flex-shrink-0" 
                                    style={{ width: '40px', height: '40px', backgroundColor: '#f1f3f5' }}
                                    onClick={() => setShowModal(false)}
                                    type="button"
                                    title="Go back"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                </button>
                                <div>
                                    <h4 className="fw-bold mb-1 text-dark fs-6">Reject Support Ticket</h4>
                                    <p className="text-muted mb-0 fs-9">Please provide a reason to continue.</p>
                                </div>
                            </div>

                            {/* Warning Alert */}
                            <div className="d-flex align-items-start p-3 mb-4 rounded-3 bg-danger-subtle border border-danger-subtle">
                                <div className="me-3 text-danger mt-1">
                                    <FontAwesomeIcon icon={faTriangleExclamation} size="lg" />
                                </div>
                                <div>
                                    <h6 className="fw-bold mb-1 text-danger fs-8">Are you sure?</h6>
                                    <p className="mb-0 text-danger-emphasis fs-9" style={{ opacity: 0.9 }}>
                                        This action cannot be undone. The customer will be notified that their request has been rejected.
                                    </p>
                                </div>
                            </div>

                            {/* Form Input */}
                            <div className="mb-4">
                                <FormInput 
                                    showHeaderLabel={true}
                                    name="RejectReason"
                                    textarea={true}
                                    errors={errors}
                                    required={true}
                                    label="Rejection Reason"
                                    placeholder="Explain why this ticket is being rejected..."
                                    validation={{...register("RejectReason", {
                                        required: 'Reject Reason is required.'
                                    })}}
                                />
                            </div>

                            {/* Action Buttons */}
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button 
                                    type="button" 
                                    className="btn btn-light fw-medium px-4" 
                                    onClick={() => setShowModal(false)}
                                    style={{ backgroundColor: '#f1f3f5', color: '#495057', border: 'none' }}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-danger fw-medium px-4"
                                >
                                    Reject Ticket
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            ) : (
                /* Original Case Files View */
                <div className="modern-form-wrapper rounded-4">
                    <div className="row">
                        <div className="col-12 col-lg-11 col-xl-10 mx-auto">
                            <div className="mb-3">
                                <button 
                                    className="btn btn-light bg-white border text-body-secondary hover-text-primary transition-base d-inline-flex align-items-center justify-content-center rounded-circle shadow-sm" 
                                    style={{ width: '32px', height: '32px' }}
                                    onClick={() => setShowCaseFileModal(false)}
                                    type="button"
                                    title="Go back"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: '13px' }} />
                                </button>
                            </div>
                            <div className="modern-card">
                                <div className="modern-card-header">
                                    <h3 className="modern-card-title">Files</h3>
                                    <p className="modern-card-desc">All files uploaded during this session.</p>
                                </div>
                                <div className="modern-card-body">
                                    {caseFiles.length <= 0 ? (
                                        <div className="text-center p-4">
                                            <span className="text-body-tertiary">No ticket file for this session chat.</span>
                                        </div>
                                    ) : (
                                        caseFiles.map((file, index) => (
                                            <FilePreview
                                                key={index}
                                                showDownloadFile={true}
                                                fileSize={file.CaseFileSize}
                                                src={file.CaseFileUrl}
                                                fileName={file.CaseFileName}
                                                disabled={true}
                                                createdDate={file.CreatedDate}
                                            />
                                        ))
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}

export default SupportCaseAdmin;